import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Mail, Instagram, Facebook, MessageCircle, Calendar } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { BRAND } from '../../data/brand';
import { useTranslation } from '../../hooks/useTranslation';
import SimpleLanguageSwitcher from '../ui/SimpleLanguageSwitcher';

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, currentLanguage } = useTranslation();
  const mobileMenuRef = useRef(null);

  // Функция для получения правильного пути в зависимости от языка
  const getLocalizedPath = (path) => {
    if (currentLanguage === 'ru') {
      return path === '/' ? '/ru' : `/ru${path}`;
    }
    return path;
  };
  
  const nav = [
    { label: t('navigation.home'), href: '/' },
    { label: t('navigation.process'), href: '/process' },
    { label: t('navigation.blog'), href: '/blog' },
    { label: t('navigation.contact'), href: '/contact' }
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Закрытие мобильного меню при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        // Проверяем, что клик не по кнопке гамбургер меню
        const hamburgerButton = event.target.closest('button[aria-label]');
        if (!hamburgerButton) {
          setIsMobileMenuOpen(false);
        }
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Закрытие меню при смене роута
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="relative z-50 sticky top-0 sm:static lg:static">
      {/* Верхняя полоска с контактами - скрыта на мобильных */}
      <div className="hidden sm:block bg-black text-white py-2 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 relative z-10">
          <div className="flex flex-col sm:flex-row items-center justify-between text-xs sm:text-sm gap-1 sm:gap-2">
            {/* Контакты слева */}
            <div className="flex items-center gap-3 sm:gap-6">
              <a 
                href={`tel:${BRAND.phone}`} 
                className="hover:text-rose-400 transition-all duration-300 flex items-center gap-1 sm:gap-2 group"
              >
                <Phone className="h-3 w-3 sm:h-4 sm:w-4 group-hover:scale-110 transition-transform" />
                <span className="hidden md:inline font-medium text-xs sm:text-sm">{BRAND.phone}</span>
                <span className="md:hidden font-medium text-xs">Позвонить</span>
              </a>
              <a 
                href={`mailto:${BRAND.email}`} 
                className="hover:text-rose-400 transition-all duration-300 flex items-center gap-1 sm:gap-2 group"
              >
                <Mail className="h-3 w-3 sm:h-4 sm:w-4 group-hover:scale-110 transition-transform" />
                <span className="hidden lg:inline font-medium text-xs sm:text-sm">{BRAND.email}</span>
                <span className="lg:hidden font-medium text-xs">Email</span>
              </a>
            </div>
            
            {/* Соцсети справа */}
            <div className="flex items-center gap-2 sm:gap-4">
              <a 
                href={BRAND.social.instagram} 
                target="_blank" 
                rel="noreferrer" 
                className="hover:text-rose-400 transition-all duration-300 flex items-center gap-1 sm:gap-2 group"
                title="Instagram"
              >
                <Instagram className="h-3 w-3 sm:h-4 sm:w-4 group-hover:scale-110 transition-transform" />
                <span className="hidden md:inline font-medium text-xs sm:text-sm">Instagram</span>
              </a>
              <a 
                href={BRAND.social.facebook} 
                target="_blank" 
                rel="noreferrer" 
                className="hover:text-rose-400 transition-all duration-300 flex items-center gap-1 sm:gap-2 group"
                title="Facebook"
              >
                <Facebook className="h-3 w-3 sm:h-4 sm:w-4 group-hover:scale-110 transition-transform" />
                <span className="hidden md:inline font-medium text-xs sm:text-sm">Facebook</span>
              </a>
              <a 
                href={BRAND.whatsapp} 
                target="_blank" 
                rel="noreferrer" 
                className="hover:text-rose-400 transition-all duration-300 flex items-center gap-1 sm:gap-2 group"
                title="WhatsApp"
              >
                <MessageCircle className="h-3 w-3 sm:h-4 sm:w-4 group-hover:scale-110 transition-transform" />
                <span className="hidden md:inline font-medium text-xs sm:text-sm">WhatsApp</span>
              </a>
              
              {/* Переключатель языков */}
              <div className="hidden sm:block">
                <SimpleLanguageSwitcher />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Основной header */}
      <motion.header 
        className={`bg-black shadow-2xl border-b transition-all duration-300 ${
          isMobileMenuOpen ? 'border-gray-600' : 'border-gray-800'
        }`}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16 lg:h-20">
            
            {/* Логотип */}
            <motion.div 
              className="flex items-center space-x-3"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <Link to={getLocalizedPath('/')} className="flex items-center">
                <img 
                  src="/images/logo/logo.png" 
                  alt={BRAND.name} 
                  className="h-16 w-auto sm:h-20 lg:h-24"
                />
              </Link>
            </motion.div>

            {/* Десктопная навигация */}
            <nav className="hidden lg:flex items-center space-x-8">
              {nav.map((item) => {
                const localizedPath = getLocalizedPath(item.href);
                return (
                  <Link
                    key={item.href}
                    to={localizedPath}
                    className={`relative font-medium transition-all duration-300 group ${
                      location.pathname === localizedPath || location.pathname === item.href
                        ? 'text-rose-400'
                        : 'text-white hover:text-rose-400'
                    }`}
                  >
                    {item.label}
                    <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-rose-400 to-rose-600 transition-all duration-300 group-hover:w-full ${
                      location.pathname === localizedPath || location.pathname === item.href ? 'w-full' : ''
                    }`}></span>
                  </Link>
                );
              })}
            </nav>

            {/* Кнопки действий */}
            <div className="hidden lg:flex items-center space-x-4">
              <motion.a
                href={getLocalizedPath('/contact')}
                className="flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all duration-300 bg-white/20 text-white hover:bg-white/30 border border-white/30"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Calendar className="h-4 w-4" />
                <span>{t('consultation')}</span>
              </motion.a>
              
              <motion.a
                href={BRAND.whatsapp}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <MessageCircle className="h-4 w-4" />
                <span>{t('book')}</span>
              </motion.a>
            </div>

            {/* Мобильная кнопка меню */}
            <motion.button
              onClick={toggleMobileMenu}
              className="lg:hidden p-1.5 sm:p-2 lg:p-3 rounded-lg sm:rounded-xl transition-all duration-300 bg-white/20 hover:bg-white/30 text-white border border-white/30"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label={t('open_menu')}
            >
              <AnimatePresence mode="wait">
                {isMobileMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>


        {/* Мобильное меню */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              ref={mobileMenuRef}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden bg-white border-t border-gray-200 shadow-lg fixed top-14 sm:top-16 left-0 right-0 max-h-[calc(100vh-3.5rem)] sm:max-h-[calc(100vh-4rem)] overflow-y-auto z-40"
            >
              <div className="px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
                {/* Мобильная навигация */}
                <nav className="space-y-2 sm:space-y-3">
                  {nav.map((item) => {
                    const localizedPath = getLocalizedPath(item.href);
                    return (
                      <Link
                        key={item.href}
                        to={localizedPath}
                        className={`block py-2 sm:py-3 px-3 sm:px-4 rounded-lg sm:rounded-xl font-medium text-base sm:text-lg transition-all duration-300 ${
                          location.pathname === localizedPath || location.pathname === item.href
                            ? 'bg-rose-50 text-rose-500 border-l-4 border-rose-500'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-rose-500'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {item.label}
                      </Link>
                    );
                  })}
                </nav>

                {/* Мобильные кнопки действий */}
                <div className="pt-3 sm:pt-4 border-t border-gray-200 space-y-2 sm:space-y-3">
                  <a
                    href={getLocalizedPath('/contact')}
                    className="flex items-center justify-center gap-2 w-full py-2 sm:py-3 px-3 sm:px-4 bg-gray-100 text-gray-700 rounded-lg sm:rounded-xl font-medium hover:bg-gray-200 transition-colors text-sm sm:text-base"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Calendar className="h-4 w-4" />
                    <span>{t('consultation')}</span>
                  </a>
                  
                  <a
                    href={BRAND.whatsapp}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-2 sm:py-3 px-3 sm:px-4 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-lg sm:rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all text-sm sm:text-base"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <MessageCircle className="h-4 w-4" />
                    <span>{t('book')}</span>
                  </a>
                </div>

                {/* Контакты в мобильном меню */}
                <div className="pt-3 sm:pt-4 border-t border-gray-200 space-y-2 sm:space-y-3">
                  <p className="font-semibold text-gray-900 text-sm sm:text-base">{t('contacts_label')}</p>
                  <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                    <a href={`tel:${BRAND.phone}`} className="flex items-center gap-2 text-gray-600 hover:text-rose-500 transition-colors">
                      <Phone className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span>{BRAND.phone}</span>
                    </a>
                    <a href={`mailto:${BRAND.email}`} className="flex items-center gap-2 text-gray-600 hover:text-rose-500 transition-colors">
                      <Mail className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="truncate">{BRAND.email}</span>
                    </a>
                  </div>
                  
                  {/* Соцсети в мобильном меню */}
                  <div className="flex items-center gap-3 sm:gap-4 pt-1 sm:pt-2">
                    <a 
                      href={BRAND.social.instagram} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="flex items-center gap-1 sm:gap-2 text-gray-600 hover:text-rose-500 transition-colors"
                    >
                      <Instagram className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="text-xs sm:text-sm">Instagram</span>
                    </a>
                    <a 
                      href={BRAND.social.facebook} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="flex items-center gap-1 sm:gap-2 text-gray-600 hover:text-rose-500 transition-colors"
                    >
                      <Facebook className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="text-xs sm:text-sm">Facebook</span>
                    </a>
                  </div>
                  
                  {/* Переключатель языков для мобильных */}
                  <div className="sm:hidden pt-1">
                    <SimpleLanguageSwitcher variant="mobile" />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>
    </div>
  );
}
